import React, { useState, useEffect } from 'react'
import { 
  Card, 
  Table, 
  Tag, 
  Button, 
  Space, 
  Typography, 
  Select, 
  Input, 
  Row, 
  Col, 
  Statistic,
  Modal,
  Form,
  message,
  Badge,
  Tooltip
} from 'antd'
import { 
  SearchOutlined, 
  FilterOutlined, 
  EyeOutlined, 
  EditOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  CloseCircleOutlined,
  CalendarOutlined,
  CopyOutlined,
  LinkOutlined,
  VideoCameraOutlined,
  SendOutlined,
} from '@ant-design/icons'
import { api } from '../services/api'
import { buildDemoBookingUrl, buildDemoRescheduleUrl } from '../config/environment'

const { Title, Text } = Typography
const { Option } = Select
const { TextArea } = Input

interface SupportRequest {
  id: string
  ticketNumber: string
  subject: string
  description: string
  priority: string
  status: string
  category: string
  requesterName: string
  requesterEmail: string
  requesterPhone?: string
  companyName?: string
  assignedTo?: string
  resolution?: string
  resolutionDate?: string
  scheduledStartAt?: string
  scheduledEndAt?: string
  googleMeetLink?: string | null
  googleCalendarHtmlLink?: string | null
  googleCalendarEventId?: string | null
  createdAt: string
  updatedAt: string
  business?: {
    id: string
    name: string
  }
  user?: {
    id: string
    firstName: string
    lastName: string
    email: string
  }
}

interface SupportStats {
  totalRequests: number
  openRequests: number
  inProgressRequests: number
  resolvedRequests: number
}

const SupportManagementPage: React.FC = () => {
  const [selectedRequest, setSelectedRequest] = useState<SupportRequest | null>(null)
  const [isDetailModalVisible, setIsDetailModalVisible] = useState(false)
  const [isUpdateModalVisible, setIsUpdateModalVisible] = useState(false)
  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    category: '',
    search: ''
  })
  const [supportRequests, setSupportRequests] = useState<SupportRequest[]>([])
  const [stats, setStats] = useState<SupportStats | null>(null)
  const [loading, setLoading] = useState(false)
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  })
  const [form] = Form.useForm()

  useEffect(() => {
    fetchSupportRequests()
    fetchStats()
  }, [filters, pagination.current, pagination.pageSize])

  const fetchSupportRequests = async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams({
        page: pagination.current.toString(),
        limit: pagination.pageSize.toString()
      })
      
      if (filters.status) params.append('status', filters.status)
      if (filters.priority) params.append('priority', filters.priority)
      if (filters.category) params.append('category', filters.category)
      if (filters.search) params.append('search', filters.search)
      
      const response = await api.get(`/support?${params.toString()}`)
      const data = response.data
      
      if (data.success) {
        setSupportRequests(data.data.supportRequests || [])
        setPagination(prev => ({
          ...prev,
          total: data.data.total || 0
        }))
      }
    } catch (error: any) {
      console.error('Failed to fetch support requests:', error)
      message.error('Failed to load support requests')
    } finally {
      setLoading(false)
    }
  }

  const fetchStats = async () => {
    try {
      const response = await api.get('/support/stats')
      const data = response.data
      
      if (data.success) {
        setStats(data.data)
      }
    } catch (error: any) {
      console.error('Failed to fetch support stats:', error)
    }
  }

  const updateSupportRequest = async (id: string, updateData: any) => {
    try {
      const response = await api.patch(`/support/${id}`, updateData)
      const data = response.data
      
      if (data.success) {
        message.success('Support request updated successfully')
        fetchSupportRequests()
        fetchStats()
        setIsUpdateModalVisible(false)
        setIsDetailModalVisible(false)
      } else {
        message.error(data.error || 'Failed to update support request')
      }
    } catch (error: any) {
      console.error('Failed to update support request:', error)
      message.error('Failed to update support request')
    }
  }

  const handleViewDetails = (record: SupportRequest) => {
    setSelectedRequest(record)
    setIsDetailModalVisible(true)
  }

  const handleUpdateStatus = (record: SupportRequest) => {
    setSelectedRequest(record)
    form.setFieldsValue({
      status: record.status,
      assignedTo: record.assignedTo,
      resolution: record.resolution
    })
    setIsUpdateModalVisible(true)
  }

  const handleUpdateSubmit = async (values: any) => {
    if (selectedRequest) {
      await updateSupportRequest(selectedRequest.id, values)
    }
  }

  const copyToClipboard = async (text: string, successMessage: string) => {
    try {
      await navigator.clipboard.writeText(text)
      message.success(successMessage)
    } catch {
      message.error('Failed to copy to clipboard')
    }
  }

  const formatDemoSchedulePt = (isoDate: string): string => {
    return new Date(isoDate).toLocaleString('en-US', {
      timeZone: 'America/Los_Angeles',
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short',
    })
  }

  const isScheduledDemo = (record: SupportRequest): boolean =>
    record.category === 'DEMO' && Boolean(record.scheduledStartAt && record.googleCalendarEventId)

  const isUnbookedDemo = (record: SupportRequest): boolean =>
    record.category === 'DEMO' && !record.scheduledStartAt && !record.googleCalendarEventId

  const getDemoRescheduleUrl = (record: Pick<SupportRequest, 'ticketNumber' | 'requesterEmail'>): string =>
    buildDemoRescheduleUrl(record.ticketNumber, record.requesterEmail)

  const getDemoBookingUrl = (record: SupportRequest): string =>
    buildDemoBookingUrl({
      ticketNumber: record.ticketNumber,
      requesterEmail: record.requesterEmail,
      requesterName: record.requesterName,
      companyName: record.companyName,
      requesterPhone: record.requesterPhone,
      description: record.description,
    })

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'OPEN': return 'red'
      case 'IN_PROGRESS': return 'blue'
      case 'RESOLVED': return 'green'
      case 'CLOSED': return 'default'
      default: return 'default'
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'URGENT': return 'red'
      case 'HIGH': return 'orange'
      case 'MEDIUM': return 'blue'
      case 'LOW': return 'green'
      default: return 'default'
    }
  }

  const columns = [
    {
      title: 'Ticket #',
      dataIndex: 'ticketNumber',
      key: 'ticketNumber',
      width: 120,
      render: (ticketNumber: string) => (
        <Text code style={{ fontSize: '12px' }}>{ticketNumber}</Text>
      )
    },
    {
      title: 'Subject',
      dataIndex: 'subject',
      key: 'subject',
      ellipsis: true,
      render: (subject: string, record: SupportRequest) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{subject}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {record.requesterName} ({record.requesterEmail})
          </div>
        </div>
      )
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      width: 120,
      render: (category: string) => (
        <Tag color="blue">{category.replace('_', ' ')}</Tag>
      )
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      width: 100,
      render: (priority: string) => (
        <Tag color={getPriorityColor(priority)}>{priority}</Tag>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>{status.replace('_', ' ')}</Tag>
      )
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      render: (date: string) => (
        <Text style={{ fontSize: '12px' }}>
          {new Date(date).toLocaleDateString()}
        </Text>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_: any, record: SupportRequest) => (
        <Space size="small">
          {isScheduledDemo(record) ? (
            <Tooltip title="Copy reschedule link">
              <Button
                type="text"
                icon={<CalendarOutlined />}
                onClick={() =>
                  copyToClipboard(
                    getDemoRescheduleUrl(record),
                    'Reschedule link copied'
                  )
                }
              />
            </Tooltip>
          ) : null}
          {isUnbookedDemo(record) ? (
            <Tooltip title="Copy schedule demo link">
              <Button
                type="text"
                icon={<SendOutlined />}
                onClick={() =>
                  copyToClipboard(
                    getDemoBookingUrl(record),
                    'Schedule demo link copied'
                  )
                }
              />
            </Tooltip>
          ) : null}
          <Tooltip title="View Details">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => handleViewDetails(record)}
            />
          </Tooltip>
          <Tooltip title="Update Status">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleUpdateStatus(record)}
            />
          </Tooltip>
        </Space>
      )
    }
  ]

  const filteredData = supportRequests.filter((request: SupportRequest) => {
    if (filters.search) {
      const searchLower = filters.search.toLowerCase()
      return (
        request.subject.toLowerCase().includes(searchLower) ||
        request.requesterName.toLowerCase().includes(searchLower) ||
        request.requesterEmail.toLowerCase().includes(searchLower) ||
        request.ticketNumber.toLowerCase().includes(searchLower)
      )
    }
    return true
  })

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>Support Request Management</Title>
      
      {/* Statistics Cards */}
      {stats && (
        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
          <Col xs={24} sm={6}>
            <Card>
              <Statistic
                title="Total Requests"
                value={stats.totalRequests}
                prefix={<ExclamationCircleOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card>
              <Statistic
                title="Open"
                value={stats.openRequests}
                valueStyle={{ color: '#cf1322' }}
                prefix={<ClockCircleOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card>
              <Statistic
                title="In Progress"
                value={stats.inProgressRequests}
                valueStyle={{ color: '#1890ff' }}
                prefix={<ClockCircleOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card>
              <Statistic
                title="Resolved"
                value={stats.resolvedRequests}
                valueStyle={{ color: '#52c41a' }}
                prefix={<CheckCircleOutlined />}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* Filters */}
      <Card style={{ marginBottom: '24px' }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={6}>
            <Input
              placeholder="Search requests..."
              prefix={<SearchOutlined />}
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            />
          </Col>
          <Col xs={24} sm={4}>
            <Select
              placeholder="Status"
              allowClear
              value={filters.status}
              onChange={(value) => setFilters({ ...filters, status: value || '' })}
              style={{ width: '100%' }}
            >
              <Option value="OPEN">Open</Option>
              <Option value="IN_PROGRESS">In Progress</Option>
              <Option value="RESOLVED">Resolved</Option>
              <Option value="CLOSED">Closed</Option>
            </Select>
          </Col>
          <Col xs={24} sm={4}>
            <Select
              placeholder="Priority"
              allowClear
              value={filters.priority}
              onChange={(value) => setFilters({ ...filters, priority: value || '' })}
              style={{ width: '100%' }}
            >
              <Option value="LOW">Low</Option>
              <Option value="MEDIUM">Medium</Option>
              <Option value="HIGH">High</Option>
              <Option value="URGENT">Urgent</Option>
            </Select>
          </Col>
          <Col xs={24} sm={4}>
            <Select
              placeholder="Category"
              allowClear
              value={filters.category}
              onChange={(value) => setFilters({ ...filters, category: value || '' })}
              style={{ width: '100%' }}
            >
              <Option value="TECHNICAL">Technical</Option>
              <Option value="BILLING">Billing</Option>
              <Option value="FEATURE_REQUEST">Feature Request</Option>
              <Option value="BUG_REPORT">Bug Report</Option>
              <Option value="GENERAL">General</Option>
            </Select>
          </Col>
          <Col xs={24} sm={6}>
            <Button
              icon={<FilterOutlined />}
              onClick={() => setFilters({ status: '', priority: '', category: '', search: '' })}
            >
              Clear Filters
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Support Requests Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey="id"
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            onChange: (page, pageSize) => {
              setPagination(prev => ({
                ...prev,
                current: page,
                pageSize: pageSize || 20
              }))
            },
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} requests`
          }}
          scroll={{ x: 1000 }}
        />
      </Card>

      {/* Detail Modal */}
      <Modal
        title={`Support Request: ${selectedRequest?.ticketNumber}`}
        open={isDetailModalVisible}
        onCancel={() => setIsDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setIsDetailModalVisible(false)}>
            Close
          </Button>,
          <Button
            key="update"
            type="primary"
            onClick={() => {
              setIsDetailModalVisible(false)
              handleUpdateStatus(selectedRequest!)
            }}
          >
            Update Status
          </Button>
        ]}
        width={800}
      >
        {selectedRequest && (
          <div>
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Text strong>Requester:</Text>
                <div>{selectedRequest.requesterName}</div>
                <div style={{ fontSize: '12px', color: '#666' }}>
                  {selectedRequest.requesterEmail}
                </div>
                {selectedRequest.requesterPhone && (
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    {selectedRequest.requesterPhone}
                  </div>
                )}
              </Col>
              <Col span={12}>
                <Text strong>Company:</Text>
                <div>{selectedRequest.companyName || 'N/A'}</div>
              </Col>
            </Row>
            
            <div style={{ marginTop: '16px' }}>
              <Text strong>Subject:</Text>
              <div>{selectedRequest.subject}</div>
            </div>
            
            <div style={{ marginTop: '16px' }}>
              <Text strong>Description:</Text>
              <div style={{ 
                marginTop: '8px', 
                padding: '12px', 
                backgroundColor: '#f5f5f5', 
                borderRadius: '4px',
                whiteSpace: 'pre-wrap'
              }}>
                {selectedRequest.description}
              </div>
            </div>
            
            <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
              <Col span={8}>
                <Text strong>Category:</Text>
                <div><Tag color="blue">{selectedRequest.category.replace('_', ' ')}</Tag></div>
              </Col>
              <Col span={8}>
                <Text strong>Priority:</Text>
                <div><Tag color={getPriorityColor(selectedRequest.priority)}>{selectedRequest.priority}</Tag></div>
              </Col>
              <Col span={8}>
                <Text strong>Status:</Text>
                <div><Tag color={getStatusColor(selectedRequest.status)}>{selectedRequest.status.replace('_', ' ')}</Tag></div>
              </Col>
            </Row>
            
            {selectedRequest.category === 'DEMO' && !selectedRequest.scheduledStartAt && (
              <div style={{ marginTop: '16px' }}>
                <Text strong>Schedule demo link</Text>
                <div style={{ marginTop: '4px' }}>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                  No demo scheduled yet. Share this link — they only need to pick a date and time. Their
                  existing demo request is used automatically.
                </Text>
                </div>
                <div
                  style={{
                    marginTop: '12px',
                    padding: '12px',
                    backgroundColor: '#f5f5f5',
                    borderRadius: '4px',
                  }}
                >
                  <div style={{ wordBreak: 'break-all' }}>
                    <Text code style={{ fontSize: '12px' }}>
                      {getDemoBookingUrl(selectedRequest)}
                    </Text>
                  </div>
                  <Space style={{ marginTop: '8px' }}>
                    <Button
                      size="small"
                      icon={<CopyOutlined />}
                      onClick={() =>
                        copyToClipboard(getDemoBookingUrl(selectedRequest), 'Schedule demo link copied')
                      }
                    >
                      Copy link
                    </Button>
                    <Button
                      size="small"
                      icon={<LinkOutlined />}
                      href={getDemoBookingUrl(selectedRequest)}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Open
                    </Button>
                  </Space>
                </div>
              </div>
            )}

            {selectedRequest.category === 'DEMO' && selectedRequest.scheduledStartAt && (
              <div style={{ marginTop: '16px' }}>
                <Text strong>Demo schedule</Text>
                <div style={{ marginTop: '8px' }}>
                  {formatDemoSchedulePt(selectedRequest.scheduledStartAt)}
                </div>
                <Space wrap style={{ marginTop: '12px' }}>
                  {selectedRequest.googleMeetLink ? (
                    <Button
                      type="link"
                      icon={<VideoCameraOutlined />}
                      href={selectedRequest.googleMeetLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ paddingLeft: 0 }}
                    >
                      Google Meet
                    </Button>
                  ) : null}
                  {selectedRequest.googleCalendarHtmlLink ? (
                    <Button
                      type="link"
                      icon={<CalendarOutlined />}
                      href={selectedRequest.googleCalendarHtmlLink}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Google Calendar
                    </Button>
                  ) : null}
                </Space>
                <div
                  style={{
                    marginTop: '12px',
                    padding: '12px',
                    backgroundColor: '#f5f5f5',
                    borderRadius: '4px',
                  }}
                >
                  <Text strong>Reschedule link</Text>
                  <div style={{ marginTop: '4px', wordBreak: 'break-all' }}>
                    <Text code style={{ fontSize: '12px' }}>
                      {getDemoRescheduleUrl(selectedRequest)}
                    </Text>
                  </div>
                  <Space style={{ marginTop: '8px' }}>
                    <Button
                      size="small"
                      icon={<CopyOutlined />}
                      onClick={() =>
                        copyToClipboard(
                          getDemoRescheduleUrl(selectedRequest),
                          'Reschedule link copied'
                        )
                      }
                    >
                      Copy link
                    </Button>
                    <Button
                      size="small"
                      icon={<LinkOutlined />}
                      href={getDemoRescheduleUrl(selectedRequest)}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Open
                    </Button>
                  </Space>
                  <div style={{ marginTop: '8px' }}>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      Share this link with the requester. Ticket and email are pre-filled; they can pick a
                      new time immediately.
                    </Text>
                  </div>
                </div>
              </div>
            )}
            
            {selectedRequest.resolution && (
              <div style={{ marginTop: '16px' }}>
                <Text strong>Resolution:</Text>
                <div style={{ 
                  marginTop: '8px', 
                  padding: '12px', 
                  backgroundColor: '#f6ffed', 
                  borderRadius: '4px',
                  border: '1px solid #b7eb8f',
                  whiteSpace: 'pre-wrap'
                }}>
                  {selectedRequest.resolution}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Update Modal */}
      <Modal
        title="Update Support Request"
        open={isUpdateModalVisible}
        onCancel={() => setIsUpdateModalVisible(false)}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleUpdateSubmit}
        >
          <Form.Item
            name="status"
            label="Status"
            rules={[{ required: true, message: 'Please select a status' }]}
          >
            <Select>
              <Option value="OPEN">Open</Option>
              <Option value="IN_PROGRESS">In Progress</Option>
              <Option value="RESOLVED">Resolved</Option>
              <Option value="CLOSED">Closed</Option>
            </Select>
          </Form.Item>
          
          <Form.Item
            name="assignedTo"
            label="Assigned To (User ID)"
          >
            <Input placeholder="Enter user ID" />
          </Form.Item>
          
          <Form.Item
            name="resolution"
            label="Resolution Notes"
          >
            <TextArea rows={4} placeholder="Enter resolution details..." />
          </Form.Item>
          
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setIsUpdateModalVisible(false)}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                Update Request
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default SupportManagementPage
